import * as React from 'react'

import Milliseconds from '../../helper/milliseconds'
import Percent from '../../helper/percent'

import moment from 'moment'
import { connect } from 'react-redux'
import { TidewaysContext } from '../../app/context'
import { Entity } from '../../app/entity'
import { EventGraphData, MarkerEvent, ReleaseEvent, TidewaysEvent } from '../../domain/Event'
import { SnapshotComparison } from '../../domain/SnapshotComparison'
import Loading from '../../helper/loading'
import { Number } from '../../helper/number'
import { ApplicationState } from '../../redux/ApplicationState'
import Tooltip from '../graph/tooltip'
import ChangedTransactionsList from './changedTransactionsList'
import SnapshotComparisonGraph from './snapshotComparisonGraph'
import { NotificationLog } from '../../errors/details/NotificationLog'
import { ActionExecution } from '../../domain/ActionExecution'
import { Box, BoxBody } from '../../ui/box'
import { ServiceSummary } from '../../domain/ServiceSummary'
import { SnapshotRange } from '../../domain/SnapshotRange'
import { layersNameToLabel, layersPhpResponseTime } from '../../domain/Layers'

export const formatRangeLabel = (range: SnapshotRange): string => {
    const startDate = moment.utc(range.startDate).local()
    const endDate = moment.utc(range.endDate).local()

    return `${startDate.format('DD.MM.YYYY')} ${startDate.format('HH:mm')} - ${endDate.format('HH:mm')}`
}

export const getAverageResponseTime = (summary: ServiceSummary, reportSlice?: { average?: number }): number =>
    summary.averageResponseTime ?? reportSlice?.average ?? 0

export const getResponseTimeChange = (before: number, after: number): number => (before > 0 ? (after - before) / before : 0)

export const getBoxTitle = (
    title: string,
    event: MarkerEvent | ReleaseEvent,
    titleAsRangeComparison: boolean | undefined,
    summaryBefore?: ServiceSummary,
    summaryAfter?: ServiceSummary,
): string => {
    if (titleAsRangeComparison && summaryBefore && summaryAfter) {
        return `Compare time ranges ${formatRangeLabel(summaryBefore.range)} to ${formatRangeLabel(summaryAfter.range)}`
    }

    return `${title}: ${event.name}`
}

// rangeBefore/rangeAfter aren't adjacent - reportMerged would draw them as one timeline with a
// misleading gap, so the two series should be charted separately instead.
export const shouldShowRangesSeparately = (snapshotComparison: SnapshotComparison): boolean =>
    !snapshotComparison.synced && !!snapshotComparison.reportSeriesBefore && !!snapshotComparison.reportSeriesAfter

export const getPhpLayerResponseTime = (
    summary: ServiceSummary,
    reportSlice?: { average?: number; downstream?: { [layer: string]: number } },
): number => {
    const summaryLayers = summary.downstream ?? {}
    const reportLayers = reportSlice?.downstream ?? {}
    const layers = Object.keys(reportLayers).length > 0 ? reportLayers : summaryLayers

    return layersPhpResponseTime(getAverageResponseTime(summary, reportSlice), layers)
}

export const getLayerResponseTime = (
    summary: ServiceSummary,
    layer: string,
    reportSlice?: { downstream?: { [layer: string]: number } },
): number => {
    const summaryLayers = summary.downstream ?? {}
    const reportLayers = reportSlice?.downstream ?? {}
    const layers = Object.keys(reportLayers).length > 0 ? reportLayers : summaryLayers

    return layers[layer] ?? 0
}

// Used only by the arbitrary time range comparison screen (`titleAsRangeComparison`) to show every
// downstream layer, not just PHP - regular event/marker screens keep the single "Average PHP Time" row.
export const getComparisonLayerNames = (
    summaryBefore?: ServiceSummary,
    summaryAfter?: ServiceSummary,
    reportSliceBefore?: { downstream?: { [layer: string]: number } },
    reportSliceAfter?: { downstream?: { [layer: string]: number } },
): string[] => {
    const names = new Set<string>()

    const addLayerNames = (summary?: ServiceSummary, reportSlice?: { downstream?: { [layer: string]: number } }) => {
        const summaryLayers = summary?.downstream ?? {}
        const reportLayers = reportSlice?.downstream ?? {}
        const layers = Object.keys(reportLayers).length > 0 ? reportLayers : summaryLayers

        Object.keys(layers).forEach(name => names.add(name))
    }

    addLayerNames(summaryBefore, reportSliceBefore)
    addLayerNames(summaryAfter, reportSliceAfter)
    names.delete('php')

    return Array.from(names).sort()
}

const responseTimeChangeStyle = (change: number) =>
    change * 100 < -5 ? { color: '#00a65a' } : change * 100 > 5 ? { color: '#f56954' } : { color: '#344ec9' }

const ResponseTimeMetric = ({ value, label, change }: { value: number; label: string; change?: number }) => (
    <p className="numbers">
        <big>
            <Milliseconds value={value * 1000} />
        </big>
        {change === undefined ? (
            <br />
        ) : (
            <small style={responseTimeChangeStyle(change)}>
                {change > 0 ? ' +' : ' '}
                <Percent value={change} />
                <br />
            </small>
        )}
        <small>{label}</small>
    </p>
)

interface State {
    activeSlot: {
        timeIndex: any
        mouse: any
    }
}

interface StateProps {
    loading: boolean
    events: TidewaysEvent[]
    snapshotComparison: SnapshotComparison
}

type Props = StateProps & {
    title: string
    router: any
    event: MarkerEvent | ReleaseEvent
    relatedAlertActionExecutions?: ActionExecution[]
    tidewaysContext: TidewaysContext
    showDetails: boolean
    // Deep-link range comparisons have no real event to name the box after - title the box after the
    // two compared ranges instead of "$title: $event.name".
    titleAsRangeComparison?: boolean
}

class PerformanceComparisonWithTransactions extends React.Component<Props, State> {
    public state: State = {
        activeSlot: {
            timeIndex: null,
            mouse: null,
        },
    }

    public render() {
        if (this.props.loading || !this.props.snapshotComparison) {
            return <Loading />
        }

        const snapshotComparison = this.props.snapshotComparison
        const improved = snapshotComparison.improvedTransactions
        const degraded = snapshotComparison.degradedTransactions
        const serviceComparison = snapshotComparison.serviceComparison
        const reportMerged = snapshotComparison.reportMerged
        const summaryBefore = snapshotComparison.summaryBefore
        const summaryAfter = snapshotComparison.summaryAfter
        const averageResponseTimeBefore = summaryBefore ? getAverageResponseTime(summaryBefore, reportMerged.slices.before) : 0
        const averageResponseTimeAfter = summaryAfter ? getAverageResponseTime(summaryAfter, reportMerged.slices.after) : 0
        const phpLayerResponseTimeBefore = summaryBefore ? getPhpLayerResponseTime(summaryBefore, reportMerged.slices.before) : 0
        const phpLayerResponseTimeAfter = summaryAfter ? getPhpLayerResponseTime(summaryAfter, reportMerged.slices.after) : 0
        const { relatedAlertActionExecutions } = this.props
        const comparisonReference = this.props.tidewaysContext.application.license.traceAggregation
            ? { type: 'event', referenceId: this.props.event.id.toString() }
            : null

        const showRangesSeparately = shouldShowRangesSeparately(snapshotComparison)
        const comparisonLayerNames = this.props.titleAsRangeComparison
            ? getComparisonLayerNames(summaryBefore, summaryAfter, reportMerged.slices.before, reportMerged.slices.after)
            : []

        return (
            <>
                <div className="box event-box jquery-tooltip-snapshot-comparison-graph">
                    <Tooltip
                        data={{ report: reportMerged }}
                        activeSlot={this.state.activeSlot}
                        percentileLabel="95% Percentile"
                        downstreamEnabled={reportMerged.hasDownstream}
                        showAverages={false}
                        parentSelector=".jquery-tooltip-snapshot-comparison-graph"
                        moveUp={20}
                    />
                    <div className="box-header">
                        <nav className="navbar navbar-toolbar">
                            <h3 className="box-title">
                                {getBoxTitle(
                                    this.props.title,
                                    this.props.event,
                                    this.props.titleAsRangeComparison,
                                    summaryBefore,
                                    summaryAfter,
                                )}
                            </h3>
                        </nav>
                    </div>
                    <div className="box-body">
                        {this.props.event.description ? <p>Description: {this.props.event.description}</p> : null}
                        {!this.props.showDetails || !reportMerged ? null : (
                            <div className="row snapshotComparison">
                                <div className={reportMerged.slices.total ? 'col-lg-10' : 'col-lg-8'}>
                                    {showRangesSeparately ? (
                                        <div className="row">
                                            <div className="col-sm-6">
                                                {summaryBefore ? (
                                                    <p className="text-muted">{formatRangeLabel(summaryBefore.range)}</p>
                                                ) : null}
                                                <SnapshotComparisonGraph
                                                    router={this.props.router}
                                                    reportMerged={snapshotComparison.reportSeriesBefore}
                                                    events={this.props.events}
                                                    changeActiveSlot={this.changeActiveSlot}
                                                    yAxisMax={snapshotComparison.yAxisMax}
                                                />
                                            </div>
                                            <div className="col-sm-6">
                                                {summaryAfter ? <p className="text-muted">{formatRangeLabel(summaryAfter.range)}</p> : null}
                                                <SnapshotComparisonGraph
                                                    router={this.props.router}
                                                    reportMerged={snapshotComparison.reportSeriesAfter}
                                                    events={this.props.events}
                                                    changeActiveSlot={this.changeActiveSlot}
                                                    yAxisMax={snapshotComparison.yAxisMax}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <SnapshotComparisonGraph
                                            router={this.props.router}
                                            reportMerged={reportMerged}
                                            events={this.props.events}
                                            changeActiveSlot={this.changeActiveSlot}
                                        />
                                    )}
                                </div>

                                {reportMerged.slices.total ? (
                                    <div className="col-lg-2 centered">
                                        <p className="numbers">
                                            <big>
                                                <Milliseconds value={reportMerged.slices.total.responseTime * 1000} />
                                            </big>
                                            <br />
                                            <small>Response Time</small>
                                        </p>

                                        <p className="numbers">
                                            <big>
                                                <Number value={reportMerged.slices.total.requests} />
                                            </big>
                                            <br />
                                            <small title="requests per minute">Requests/min</small>
                                        </p>

                                        {!reportMerged.errors ? null : (
                                            <p className="numbers">
                                                <big>
                                                    <Percent value={reportMerged.slices.total.errorRate / 100} />
                                                </big>
                                                <br />
                                                <small>Failures</small>
                                            </p>
                                        )}
                                    </div>
                                ) : null}

                                {summaryBefore ? (
                                    <div className="col-lg-2 centered">
                                        <p>
                                            <big>Before</big>
                                            <br />
                                            <small className="text-muted">{formatRangeLabel(summaryBefore.range)}</small>
                                        </p>

                                        <ResponseTimeMetric value={summaryBefore.percentileResponse} label="95% Percentile" />
                                        <ResponseTimeMetric value={averageResponseTimeBefore} label="Average Response" />
                                        <ResponseTimeMetric value={phpLayerResponseTimeBefore} label="Average PHP Time" />
                                        {comparisonLayerNames.map(layer => (
                                            <ResponseTimeMetric
                                                key={layer}
                                                value={getLayerResponseTime(summaryBefore, layer, reportMerged.slices.before)}
                                                label={layersNameToLabel(layer)}
                                            />
                                        ))}

                                        <p className="numbers">
                                            <big>
                                                <Number value={summaryBefore.requests} />
                                            </big>
                                            <br />
                                            <small title="requests per minute">Requests/min</small>
                                        </p>

                                        {!reportMerged.errors ? null : (
                                            <p className="numbers">
                                                <big>
                                                    <Percent value={summaryBefore.errorRate / 100} />
                                                </big>
                                                <br />
                                                <small>Failures</small>
                                            </p>
                                        )}
                                    </div>
                                ) : null}

                                {summaryAfter ? (
                                    <div className="col-lg-2 centered">
                                        <p>
                                            <big>After</big>
                                            <br />
                                            <small className="text-muted">{formatRangeLabel(summaryAfter.range)}</small>
                                        </p>

                                        <ResponseTimeMetric
                                            value={summaryAfter.percentileResponse}
                                            label="95% Percentile"
                                            change={
                                                serviceComparison?.responseTimeChange ??
                                                getResponseTimeChange(
                                                    summaryBefore?.percentileResponse ?? 0,
                                                    summaryAfter.percentileResponse,
                                                )
                                            }
                                        />

                                        <ResponseTimeMetric
                                            value={averageResponseTimeAfter}
                                            label="Average Response"
                                            change={getResponseTimeChange(averageResponseTimeBefore, averageResponseTimeAfter)}
                                        />

                                        <ResponseTimeMetric
                                            value={phpLayerResponseTimeAfter}
                                            label="Average PHP Time"
                                            change={getResponseTimeChange(phpLayerResponseTimeBefore, phpLayerResponseTimeAfter)}
                                        />
                                        {comparisonLayerNames.map(layer => {
                                            const layerResponseTimeBefore = summaryBefore
                                                ? getLayerResponseTime(summaryBefore, layer, reportMerged.slices.before)
                                                : 0
                                            const layerResponseTimeAfter = getLayerResponseTime(
                                                summaryAfter,
                                                layer,
                                                reportMerged.slices.after,
                                            )

                                            return (
                                                <ResponseTimeMetric
                                                    key={layer}
                                                    value={layerResponseTimeAfter}
                                                    label={layersNameToLabel(layer)}
                                                    change={getResponseTimeChange(layerResponseTimeBefore, layerResponseTimeAfter)}
                                                />
                                            )
                                        })}

                                        <p className="numbers">
                                            <big>
                                                <Number value={summaryAfter.requests} />
                                            </big>
                                            {serviceComparison ? (
                                                <small
                                                    style={
                                                        serviceComparison.requestsChange * 100 < -10 ||
                                                        serviceComparison.requestsChange * 100 > 10
                                                            ? { color: '#f56954' }
                                                            : serviceComparison.requestsChange * 100 > 5 ||
                                                              serviceComparison.requestsChange * 100 < -5
                                                            ? { color: '#00a65a' }
                                                            : { color: '#344ec9' }
                                                    }
                                                >
                                                    {serviceComparison.requestsChange > 0 ? ' +' : ' '}
                                                    <Percent value={serviceComparison.requestsChange} />
                                                    <br />
                                                </small>
                                            ) : (
                                                <br />
                                            )}
                                            <small title="requests per minute">Requests/min</small>
                                        </p>

                                        {!reportMerged.errors ? null : (
                                            <p className="numbers">
                                                <big>
                                                    <Percent value={summaryAfter.errorRate / 100} />
                                                </big>
                                                {serviceComparison ? (
                                                    <small
                                                        style={
                                                            serviceComparison.errorsChange < 0
                                                                ? { color: '#00a65a' }
                                                                : serviceComparison.errorsChange > 5
                                                                ? { color: '#f56954' }
                                                                : { color: '#344ec9' }
                                                        }
                                                    >
                                                        {serviceComparison.errorsChange > 0 ? ' +' : ' '}
                                                        <Percent value={serviceComparison.errorsChange} />
                                                        <br />
                                                    </small>
                                                ) : (
                                                    <br />
                                                )}
                                                <small>Failures</small>
                                            </p>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                </div>
                {!this.props.showDetails || !snapshotComparison ? null : (
                    <div className="row">
                        <div className="col-sm-12">
                            <ul className="nav nav-pills nav-pills-tideways">
                                <li className="active">
                                    <a href="#transactions" data-target="#transactions" data-toggle="tab">
                                        Transactions
                                    </a>
                                </li>
                                <li>
                                    <a href="#notificationLog" data-target="#notificationLog" data-toggle="tab">
                                        Notification Log
                                    </a>
                                </li>
                            </ul>

                            <Box>
                                <BoxBody noPadding>
                                    <div className="tab-content no-padding">
                                        <div className="tab-pane active" id="transactions">
                                            <ChangedTransactionsList
                                                transactions={degraded}
                                                type={'degraded'}
                                                isTideways6={this.props.tidewaysContext.organization.isTideways6}
                                                comparisonReference={comparisonReference}
                                            />
                                            <ChangedTransactionsList
                                                transactions={improved}
                                                type={'improved'}
                                                isTideways6={this.props.tidewaysContext.organization.isTideways6}
                                                comparisonReference={comparisonReference}
                                            />
                                        </div>
                                        <div className="tab-pane" id="notificationLog">
                                            <NotificationLog actionExecutions={relatedAlertActionExecutions} />
                                        </div>
                                    </div>
                                </BoxBody>
                            </Box>
                        </div>
                    </div>
                )}
            </>
        )
    }

    private changeActiveSlot = activeSlot => this.setState({ activeSlot })
}

export default connect((globalState: ApplicationState) => {
    const routeParameters = globalState.tidewaysContext.route.parameters

    // Depending on which route we are using the currently selected event id might be given as "event" or "error"
    const key = routeParameters.event || routeParameters.error

    const eventGraph = globalState.events.eventGraphs[key] || (new Entity() as Entity<EventGraphData>)

    return {
        loading: eventGraph.loading,
        snapshotComparison: eventGraph.data?.snapshotComparison,
        events: eventGraph.data?.events,
        ...eventGraph.data,
    }
})(PerformanceComparisonWithTransactions)
